<script setup lang="ts">
import CreditCardIcon from '@/assets/icons/CreditCardIcon.vue';
import DocumentIcon from '@/assets/icons/DocumentIcon.vue';
import StripeIcon from '@/assets/icons/StripeIcon.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import BillingCheckoutIllustrationPanel from './BillingCheckoutIllustrationPanel.vue';
import BillingCheckoutMethodSelectionCard from './BillingCheckoutMethodSelectionCard.vue';
import { ref, watch, type Component } from 'vue';

type PaymentMethod = 'online' | 'invoice';

const props = withDefaults(
  defineProps<{
    modelValue?: PaymentMethod;
    disabled?: boolean;
  }>(),
  {
    modelValue: 'online',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: PaymentMethod];
  back: [];
  continue: [value: PaymentMethod];
}>();

const selectedMethod = ref<PaymentMethod>(props.modelValue);

watch(
  () => props.modelValue,
  (method) => {
    selectedMethod.value = method;
  },
);

const selectMethod = (method: PaymentMethod): void => {
  selectedMethod.value = method;
  emit('update:modelValue', method);
};

const paymentMethodOptions: Array<{
  method: PaymentMethod;
  titleKey: string;
  descriptionKey: string;
  icon: Component;
}> = [
  {
    method: 'online',
    titleKey: 'billing.invoicePayment.onlineTitle',
    descriptionKey: 'billing.invoicePayment.onlineDescription',
    icon: CreditCardIcon,
  },
  {
    method: 'invoice',
    titleKey: 'billing.invoicePayment.invoiceTitle',
    descriptionKey: 'billing.invoicePayment.invoiceDescription',
    icon: DocumentIcon,
  },
];
</script>

<template>
  <form
    class="flex h-full min-h-0 overflow-hidden bg-surface-primary max-md:flex-col"
    @submit.prevent="emit('continue', selectedMethod)"
  >
    <BillingCheckoutIllustrationPanel />

    <div class="flex h-full min-w-0 flex-1 flex-col bg-surface-primary px-xxl py-xxl max-md:p-lg max-md:pt-xxl">
      <div class="min-h-0 flex-1 overflow-y-auto">
        <div class="flex h-full min-h-[420px] flex-col gap-xl max-md:min-h-0 max-md:gap-lg">
          <h1 class="text-title text-primary max-md:text-heading">
            {{ $t('billing.invoicePayment.methodTitle') }}
          </h1>

          <div
            class="flex flex-1 flex-col gap-md"
            role="radiogroup"
            :aria-label="$t('billing.invoicePayment.methodTitle')"
          >
            <BillingCheckoutMethodSelectionCard
              v-for="option in paymentMethodOptions"
              :key="option.method"
              :title="$t(option.titleKey)"
              :description="$t(option.descriptionKey)"
              :icon="option.icon"
              :is-selected="selectedMethod === option.method"
              :disabled="props.disabled"
              @select="selectMethod(option.method)"
            />

            <div class="flex flex-wrap items-center gap-sm text-body leading-[170%] text-secondary max-md:text-footer">
              <span>{{ $t('billing.invoicePayment.stripePrefix') }}</span>
              <StripeIcon class="h-icon-sm w-[49px] shrink-0" />
              <span>{{ $t('billing.invoicePayment.stripeSuffix') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex shrink-0 items-center justify-end gap-md pt-md max-md:pb-mobile-safe-bottom-lg">
        <CustomButton type="button" theme="secondary" :disabled="props.disabled" @click="emit('back')">
          {{ $t('general.back') }}
        </CustomButton>
        <CustomButton class="w-min justify-center whitespace-nowrap" :disabled="props.disabled" type="submit">
          {{ $t('general.continue') }}
        </CustomButton>
      </div>
    </div>
  </form>
</template>
